import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileIcon } from '../../components/icons/ChatCategoryIcons.js';
import SourcesButton from '../../components/chat/SourcesButton';
import { getAssessmentPlanHistory, getAssessmentPlanRecord, deleteAssessmentPlanRecord, updateAssessmentPlanRecordMeta } from '../../api/assessmentPlanApi';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../components/Toast';
import '../saenggibu/SaenggibuHistory.css';
import './AssessmentPlanHistory.css';
import './AssessmentPlanPage.css';

const getDateGroup = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  if (date >= today) return '오늘';
  if (date >= yesterday) return '어제';
  if (date >= weekAgo) return '이번 주';
  if (date >= monthStart) return '이번 달';
  return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
};

const getCalendarYear = (dateStr) => new Date(dateStr).getFullYear();

const isInSemesterRange = (dateStr, year, semester) => {
  const date = new Date(dateStr);
  if (semester === 1) {
    const start = new Date(year, 2, 1);
    const end = new Date(year, 8, 0, 23, 59, 59);
    return date >= start && date <= end;
  }

  const start = new Date(year, 8, 1);
  const end = new Date(year + 1, 2, 0, 23, 59, 59);
  return date >= start && date <= end;
};

const HISTORY_PAGE_SIZE = 100;
const MAX_LOADED_HISTORY_RECORDS = 1000;

const PLAN_TYPE_LABELS = {
  assessment: '평가계획서',
  lesson_plan: '수업 지도안',
  project: '프로젝트 수업',
  essay_eval: '서논술형 평가',
  inquiry: '탐구질문',
  autonomous: '자율시간',
};

const PLAN_TYPE_INITIALS = {
  assessment: '평',
  lesson_plan: '수',
  project: '프',
  essay_eval: '논',
  inquiry: '탐',
  autonomous: '자',
};

const PLAN_TYPE_COUNT_LABELS = {
  assessment: '항목',
  lesson_plan: '단계',
  project: '차시',
  essay_eval: '문항',
  inquiry: '질문',
  autonomous: '차시',
};

const getPlanType = (record) => record?.plan_type || 'assessment';

const getDefaultExpandedItemIds = (record) => {
  if (getPlanType(record) !== 'assessment') return [];
  const items = record?.plan_structured?.items || [];
  return items.length > 0 ? [0] : [];
};

const OVERVIEW_LABEL_MAP = {
  purpose: '목적', policy: '방침', precautions: '유의사항',
  direction_guidelines: '평가 방향', 'direction guidelines': '평가 방향',
  evaluation_ratio: '평가 비율', 'evaluation ratio': '평가 비율',
  summary: '요약', achievement_levels: '성취수준', 'achievement levels': '성취수준',
  ongoing_evaluation: '과정중심평가', diagnostic_activities: '진단활동',
  evaluation_results_utilization: '평가결과 활용', remote_learning_evaluation: '원격수업 평가',
  evaluation_notification: '평가 안내', evaluation_areas: '평가 영역',
  evaluation_methods_general: '평가 방법',
};

const formatOverviewValue = (val) => {
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) {
    return val.map((v) => {
      if (typeof v === 'string') return `• ${v}`;
      if (typeof v === 'object' && v !== null) {
        const parts = Object.entries(v).filter(([, v2]) => typeof v2 === 'string').map(([k, v2]) => `${k}: ${v2}`);
        return parts.length ? `• ${parts.join(', ')}` : `• ${JSON.stringify(v)}`;
      }
      return `• ${String(v)}`;
    }).join('\n');
  }
  if (typeof val === 'object' && val !== null) {
    return Object.entries(val).map(([k, v]) => `${OVERVIEW_LABEL_MAP[k] || k.replace(/_/g, ' ')}: ${formatOverviewValue(v)}`).join('\n');
  }
  return String(val);
};

const safeOverviewText = (overview) => {
  if (!overview) return '';
  if (typeof overview === 'string') return overview;
  if (typeof overview === 'object' && !Array.isArray(overview)) {
    return Object.entries(overview)
      .map(([key, val]) => {
        const label = OVERVIEW_LABEL_MAP[key] || key.replace(/_/g, ' ');
        return `[${label}]\n${formatOverviewValue(val)}`;
      })
      .join('\n\n');
  }
  if (Array.isArray(overview)) return formatOverviewValue(overview);
  return String(overview);
};

const AssessmentPlanHistory = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const { showToast } = useToast();

  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [expandedItemIdsByRecord, setExpandedItemIdsByRecord] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('전체');
  const [selectedSemester, setSelectedSemester] = useState('전체');
  const [selectedSubject, setSelectedSubject] = useState('전체');
  const [selectedGrade, setSelectedGrade] = useState('전체');
  const [selectedPlanType, setSelectedPlanType] = useState('전체');
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [savingTitleId, setSavingTitleId] = useState(null);

  const isProUser = subscriptionStatus?.isPro || false;

  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (d >= today) return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const availableYears = useMemo(() => {
    const yearSet = new Set();
    historyData.forEach(r => yearSet.add(getCalendarYear(r.created_at)));
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [historyData]);

  const baseFilteredData = useMemo(() => {
    let data = historyData;
    if (selectedYear !== '전체') {
      const yearNum = parseInt(selectedYear, 10);
      if (selectedSemester !== '전체') {
        const semesterNum = parseInt(selectedSemester, 10);
        data = data.filter(r => isInSemesterRange(r.created_at, yearNum, semesterNum));
      } else {
        data = data.filter(r => getCalendarYear(r.created_at) === yearNum);
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      data = data.filter(r => {
        const haystack = [
          r.custom_title,
          r.preview,
          r.subject,
          r.grade,
          r.semester,
          r.plan_structured?.title,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      });
    }
    return data;
  }, [historyData, selectedYear, selectedSemester, searchQuery]);

  const planTypeCounts = useMemo(() => {
    const counts = { 전체: baseFilteredData.length };
    Object.keys(PLAN_TYPE_LABELS).forEach((key) => { counts[key] = 0; });
    baseFilteredData.forEach((record) => {
      const type = record.plan_type || 'assessment';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [baseFilteredData]);

  const availableSubjects = useMemo(() => {
    const set = new Set();
    baseFilteredData.forEach(r => {
      const subj = r.subject || r.plan_structured?.subject;
      if (subj) set.add(subj);
    });
    return Array.from(set).sort();
  }, [baseFilteredData]);

  const availableGrades = useMemo(() => {
    const set = new Set();
    baseFilteredData.forEach(r => {
      const grade = r.grade || r.plan_structured?.grade;
      if (grade) set.add(grade);
    });
    return Array.from(set).sort();
  }, [baseFilteredData]);

  const filteredData = useMemo(() => {
    let data = baseFilteredData;
    if (selectedPlanType !== '전체') {
      data = data.filter(r => (r.plan_type || 'assessment') === selectedPlanType);
    }
    if (selectedSubject !== '전체') {
      data = data.filter(r => {
        const subj = r.subject || r.plan_structured?.subject || '';
        return subj === selectedSubject;
      });
    }
    if (selectedGrade !== '전체') {
      data = data.filter(r => {
        const grade = r.grade || r.plan_structured?.grade || '';
        return grade === selectedGrade;
      });
    }
    return data;
  }, [baseFilteredData, selectedSubject, selectedGrade, selectedPlanType]);

  const groupedData = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    filteredData.forEach(record => {
      const group = getDateGroup(record.created_at);
      if (group !== currentGroup) {
        groups.push({ type: 'header', label: group });
        currentGroup = group;
      }
      groups.push({ type: 'record', data: record });
    });
    return groups;
  }, [filteredData]);

  const loadHistory = useCallback(async (page = 0, append = false) => {
    try {
      setIsLoading(true);
      const response = await getAssessmentPlanHistory(HISTORY_PAGE_SIZE, page * HISTORY_PAGE_SIZE, isProUser);
      const records = response.records || [];
      setHistoryData(prev => append ? [...prev, ...records] : records);
      setHasMore(Boolean(response.has_more));
      setCurrentPage(page);
    } catch {
      showToast('기록을 불러오지 못했어요');
    } finally {
      setIsLoading(false);
    }
  }, [isProUser, showToast]);

  useEffect(() => {
    if (currentUser) loadHistory(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleLoadMore = useCallback(() => {
    if (historyData.length >= MAX_LOADED_HISTORY_RECORDS) {
      setHasMore(false);
      showToast(`화면에서는 최대 ${MAX_LOADED_HISTORY_RECORDS.toLocaleString()}개 기록까지만 불러올 수 있어요`);
      return;
    }
    loadHistory(currentPage + 1, true);
  }, [currentPage, historyData.length, loadHistory, showToast]);

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    const targetIds = filteredData.map(r => r.id);
    const allSelected = targetIds.length > 0 && targetIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : targetIds);
  }, [filteredData, selectedIds]);

  const handleOpenDeleteConfirm = useCallback(() => {
    if (selectedIds.length === 0) { showToast('삭제할 기록을 선택해 주세요'); return; }
    setShowDeleteConfirm(true);
  }, [selectedIds, showToast]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const idsToDelete = [...selectedIds];
    const count = idsToDelete.length;
    const prevData = historyData;

    setHistoryData(prev => prev.filter(r => !idsToDelete.includes(r.id)));
    setSelectedIds([]);
    setExpandedIds(prev => prev.filter(id => !idsToDelete.includes(id)));
    setShowDeleteConfirm(false);
    showToast(`${count}개 기록을 삭제했어요`);

    try {
      await Promise.all(idsToDelete.map(id => deleteAssessmentPlanRecord(id)));
    } catch {
      setHistoryData(prevData);
      showToast('삭제 중 문제가 생겼어요. 다시 시도해 주세요');
    }
  }, [selectedIds, historyData, showToast]);

  const toggleExpand = useCallback(async (recordId) => {
    if (expandedIds.includes(recordId)) {
      setExpandedIds(prev => prev.filter(id => id !== recordId));
      return;
    }

    const record = historyData.find(r => r.id === recordId);
    if (record?.plan_structured) {
      setExpandedItemIdsByRecord(prev => ({
        ...prev,
        [recordId]: prev[recordId] || getDefaultExpandedItemIds(record),
      }));
      setExpandedIds(prev => [...prev, recordId]);
    } else {
      try {
        const response = await getAssessmentPlanRecord(recordId);
        const hydratedRecord = { ...record, ...response };
        setHistoryData(prev => prev.map(r =>
          r.id === recordId ? { ...r, ...response } : r
        ));
        setExpandedItemIdsByRecord(prev => ({
          ...prev,
          [recordId]: prev[recordId] || getDefaultExpandedItemIds(hydratedRecord),
        }));
        setExpandedIds(prev => [...prev, recordId]);
      } catch {
        showToast('상세 정보를 불러오지 못했어요');
      }
    }
  }, [expandedIds, historyData, showToast]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('클립보드에 복사했어요');
    } catch { showToast('복사에 실패했어요'); }
  };

  const getRevisionCount = (record) => {
    if (Number(record.revision_count || 0) > 0) return Number(record.revision_count);
    return Array.isArray(record.revision_history) ? record.revision_history.length : 0;
  };

  const getPlanTypeLabel = (record) => PLAN_TYPE_LABELS[getPlanType(record)] || '평가계획서';

  const getRecordCountLabel = (record) => {
    const type = getPlanType(record);
    const count = Number(record.item_count || 0);
    if (count <= 0) return PLAN_TYPE_COUNT_LABELS[type] || '항목';
    return `${count}개 ${PLAN_TYPE_COUNT_LABELS[type] || '항목'}`;
  };

  const getCardTitle = (record) => {
    const customTitle = (record.custom_title || '').trim();
    if (customTitle) return customTitle.length > 50 ? `${customTitle.substring(0, 50)}...` : customTitle;
    const title = record.plan_structured?.title || '';
    if (title) return title.length > 50 ? title.substring(0, 50) + '...' : title;
    const preview = (record.preview || '').replace(/\n+/g, ' ').trim();
    if (!preview) return '(제목 없음)';
    return preview.length > 50 ? preview.substring(0, 50) + '...' : preview;
  };

  const getCardSubtitle = (record) => {
    const autoTitle = (record.plan_structured?.title || record.preview || '').replace(/\n+/g, ' ').trim();
    const parts = [
      record.subject || record.plan_structured?.subject,
      record.grade || record.plan_structured?.grade,
      record.semester || record.plan_structured?.semester,
      getRecordCountLabel(record),
    ].filter(Boolean);
    const revisionCount = getRevisionCount(record);
    if (revisionCount > 0) parts.push(`${revisionCount}회 수정`);
    if ((record.custom_title || '').trim() && autoTitle) {
      parts.unshift(autoTitle.length > 28 ? `${autoTitle.substring(0, 28)}...` : autoTitle);
    }
    return parts.join(' · ');
  };

  const getPlanInitial = (record) => PLAN_TYPE_INITIALS[getPlanType(record)] || '평';

  const toggleAssessmentItem = useCallback((recordId, itemIdx) => {
    setExpandedItemIdsByRecord(prev => {
      const current = prev[recordId] || [];
      const next = current.includes(itemIdx)
        ? current.filter(idx => idx !== itemIdx)
        : [...current, itemIdx];
      return { ...prev, [recordId]: next };
    });
  }, []);

  const getPointsSummary = (record) => {
    const items = record.plan_structured?.items || [];
    const byCategory = {};
    let total = 0;
    items.forEach((item) => {
      const pts = Number(item.points) || 0;
      const category = item.category || item.type || '기타';
      byCategory[category] = (byCategory[category] || 0) + pts;
      total += pts;
    });
    return { byCategory, total };
  };

  const formatAchievementStandards = (standards) => {
    if (!standards) return '';
    if (typeof standards === 'string') return standards;
    if (Array.isArray(standards)) return standards.join(', ');
    return JSON.stringify(standards);
  };

  const handleStartTitleEdit = (record) => {
    setEditingTitleId(record.id);
    setEditingTitleValue((record.custom_title || getCardTitle(record)).replace(/\.\.\.$/, ''));
  };

  const handleCancelTitleEdit = () => {
    setEditingTitleId(null);
    setEditingTitleValue('');
  };

  const handleSaveTitleEdit = useCallback(async (recordId) => {
    const nextTitle = editingTitleValue.trim().slice(0, 80);
    const prevData = historyData;

    setSavingTitleId(recordId);
    setHistoryData(prev => prev.map(record =>
      record.id === recordId ? { ...record, custom_title: nextTitle } : record
    ));
    setEditingTitleId(null);
    setEditingTitleValue('');

    try {
      await updateAssessmentPlanRecordMeta(recordId, { custom_title: nextTitle });
      showToast(nextTitle ? '기록 이름을 저장했어요' : '기록 이름을 기본값으로 돌렸어요');
    } catch {
      setHistoryData(prevData);
      showToast('기록 이름을 저장하지 못했어요. 다시 시도해 주세요');
    } finally {
      setSavingTitleId(null);
    }
  }, [editingTitleValue, historyData, showToast]);

  const renderAssessmentItem = (record, planItem, pIdx) => {
    const expandedItems = expandedItemIdsByRecord[record.id] || getDefaultExpandedItemIds(record);
    const isItemExpanded = expandedItems.includes(pIdx);
    const itemTitle = planItem.name || planItem.question || `${pIdx + 1}번째 항목`;

    return (
      <div key={planItem.id || pIdx} className={`aph-item ${isItemExpanded ? 'aph-item--expanded' : ''}`}>
        <button
          type="button"
          className="aph-item-toggle"
          aria-expanded={isItemExpanded}
          onClick={() => toggleAssessmentItem(record.id, pIdx)}
        >
          <span className="aph-item-summary">
            <span className="aph-item-name">{itemTitle}</span>
            {planItem.type && <span className="aph-item-badge">{planItem.type}</span>}
            {planItem.points !== undefined && <span className="aph-item-points">{planItem.points}점</span>}
            {planItem.period && <span className="aph-item-period">{planItem.period}</span>}
          </span>
          <svg className="aph-item-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {isItemExpanded && (
          <div className="aph-item-detail">
            {(planItem.domain || planItem.method) && (
              <div className="aph-item-detail-meta">
                {planItem.domain && <span>영역 {planItem.domain}</span>}
                {planItem.method && <span>방법 {planItem.method}</span>}
                {planItem.ratio && <span>비율 {planItem.ratio}</span>}
              </div>
            )}
            {planItem.achievement_standards && (
              <div className="aph-achievement">
                <span className="aph-achievement-label">성취기준</span>
                <span className="aph-achievement-text">{formatAchievementStandards(planItem.achievement_standards)}</span>
              </div>
            )}
            {planItem.details && (
              <div className="aph-achievement">
                <span className="aph-achievement-label">세부내용</span>
                <span className="aph-achievement-text">{planItem.details}</span>
              </div>
            )}
            {planItem.teaching_connection && (
              <div className="aph-achievement">
                <span className="aph-achievement-label">수업연계</span>
                <span className="aph-achievement-text">{planItem.teaching_connection}</span>
              </div>
            )}
            {planItem.scoring_criteria?.length > 0 && (
              <div className="aph-criteria">
                {planItem.scoring_criteria.map((c, cIdx) => (
                  <div key={cIdx} className="aph-criteria-row">
                    <span className="aph-criteria-level">{c.level}</span>
                    <span className="aph-criteria-score">{c.score_range}</span>
                    <span className="aph-criteria-desc">{c.description}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };



  if (!currentUser) {
    return (
      <div className="sh-empty">
        <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
        <h3 className="sh-empty-title">로그인이 필요해요</h3>
        <p className="sh-empty-desc">계획서 기록을 보려면 먼저 로그인해 주세요</p>
        <button className="sh-empty-btn" onClick={() => window.location.href = '/auth?tab=login&redirect=/assessment-plan-history'}>
          로그인하기
        </button>
      </div>
    );
  }

  return (
    <div className="sh-container">
      <div className="sh-header">
        <div className="sh-header-row">
          <h2 className="sh-title">
            계획서 기록
            {historyData.length > 0 && (
              <span className="sh-title-count">
                {selectedIds.length > 0 ? `${selectedIds.length}개 선택` : `${filteredData.length}개`}
              </span>
            )}
          </h2>
        </div>
      </div>

      {!isLoading && historyData.length > 0 && (
        <div className="sh-search-row">
          <div className="sh-search-wrap">
            <svg className="sh-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="sh-search-input"
              type="text"
              placeholder="교과, 제목으로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="sh-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
            )}
          </div>
          {availableYears.length > 0 && (
            <div className="sh-semester-selects">
              <select
                className={`sh-select ${selectedYear !== '전체' ? 'sh-select--active' : ''}`}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  if (e.target.value === '전체') setSelectedSemester('전체');
                }}
              >
                <option value="전체">전체 년도</option>
                {availableYears.map(y => (
                  <option key={y} value={String(y)}>{y}년</option>
                ))}
              </select>
              <select
                className={`sh-select ${selectedSemester !== '전체' ? 'sh-select--active' : ''}`}
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
                disabled={selectedYear === '전체'}
              >
                <option value="전체">전체 학기</option>
                <option value="1">1학기</option>
                <option value="2">2학기</option>
              </select>
            </div>
          )}
        </div>
      )}

      {!isLoading && historyData.length > 0 && (
        <div className="sh-filter-tabs">
          <button
            className={`sh-filter-tab ${selectedPlanType === '전체' ? 'sh-filter-tab--active' : ''}`}
            onClick={() => setSelectedPlanType('전체')}
          >
            전체
            <span className="sh-filter-tab-count">{planTypeCounts.전체 || 0}</span>
          </button>
          {Object.entries(PLAN_TYPE_LABELS).map(([k, v]) => (
            <button
              key={k}
              className={`sh-filter-tab ${selectedPlanType === k ? 'sh-filter-tab--active' : ''}`}
              onClick={() => setSelectedPlanType(k)}
            >
              {v}
              <span className="sh-filter-tab-count">{planTypeCounts[k] || 0}</span>
            </button>
          ))}
        </div>
      )}

      {!isLoading && historyData.length > 0 && (availableSubjects.length > 1 || availableGrades.length > 1) && (
        <div className="aph-filter-row">
          {availableSubjects.length > 1 && (
            <select
              className={`sh-select ${selectedSubject !== '전체' ? 'sh-select--active' : ''}`}
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="전체">전체 교과</option>
              {availableSubjects.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          {availableGrades.length > 1 && (
            <select
              className={`sh-select ${selectedGrade !== '전체' ? 'sh-select--active' : ''}`}
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
            >
              <option value="전체">전체 학년</option>
              {availableGrades.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {searchQuery.trim() && (
        <div className="sh-search-result">
          &lsquo;{searchQuery.trim()}&rsquo; 검색 결과 {filteredData.length}개
        </div>
      )}

      {!isLoading && filteredData.length > 0 && (
        <div className="sh-actions">
          <button className="sh-action-btn sh-action-btn--select" onClick={handleToggleSelectAll}>
            {filteredData.length > 0 && filteredData.every(r => selectedIds.includes(r.id)) ? '선택 해제' : '전체 선택'}
          </button>
          {selectedIds.length > 0 && (
            <button className="sh-action-btn sh-action-btn--delete" onClick={handleOpenDeleteConfirm}>
              {`삭제 (${selectedIds.length})`}
            </button>
          )}
        </div>
      )}

      {isLoading && historyData.length === 0 && (
        <div className="sh-list">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="sh-skeleton-card">
              <div className="sh-skeleton-line sh-skeleton-line--medium" />
              <div className="sh-skeleton-line sh-skeleton-line--short" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && historyData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">아직 기록이 없어요</h3>
          <p className="sh-empty-desc">계획서를 만들면 여기에 저장돼요.</p>
          <button className="sh-empty-btn" onClick={() => window.location.href = '/assessment-plan'}>
            만들러 가기
          </button>
        </div>
      )}

      {!isLoading && historyData.length > 0 && filteredData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">검색 결과가 없어요</h3>
          <p className="sh-empty-desc">다른 검색어나 필터를 시도해보세요.</p>
        </div>
      )}

      {filteredData.length > 0 && (
        <div className="sh-list">
          {groupedData.map((item, idx) => {
            if (item.type === 'header') {
              return <div key={`h-${idx}`} className="sh-date-group">{item.label}</div>;
            }
            const record = item.data;
            const isExpanded = expandedIds.includes(record.id);
            const isSelected = selectedIds.includes(record.id);

            return (
              <div
                key={record.id}
                className={`sh-card ${isSelected ? 'sh-card--selected' : ''}`}
              >
                <div
                  className="sh-card-body"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpand(record.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand(record.id);
                    }
                  }}
                >
                  <div
                    className={`sh-checkbox ${isSelected ? 'sh-checkbox--checked' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleSelect(record.id); }}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className={`sh-card-initial aph-card-initial--${getPlanType(record)}`}>
                    {getPlanInitial(record)}
                  </div>
                  <div className="sh-card-info">
                    <div className="sh-card-title-row">
                      {editingTitleId === record.id ? (
                        <span className="sh-record-title-edit" onClick={(e) => e.stopPropagation()}>
                          <input
                            className="sh-record-title-input"
                            value={editingTitleValue}
                            onChange={(e) => setEditingTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') handleSaveTitleEdit(record.id);
                              if (e.key === 'Escape') handleCancelTitleEdit();
                            }}
                            placeholder="예: 1학기 국어 평가계획서"
                            maxLength={80}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="sh-record-title-action sh-record-title-action--save"
                            disabled={savingTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveTitleEdit(record.id);
                            }}
                          >
                            저장
                          </button>
                          <button
                            type="button"
                            className="sh-record-title-action"
                            disabled={savingTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelTitleEdit();
                            }}
                          >
                            취소
                          </button>
                        </span>
                      ) : (
                        <>
                          <p className="sh-card-title">
                            {(record.plan_type && record.plan_type !== 'assessment') && (
                              <span className="aph-type-badge">{getPlanTypeLabel(record)}</span>
                            )}
                            {(record.has_hwp_result || record.hwp_result?.file_id) && (
                              <span className="aph-hwp-badge">한글 문서</span>
                            )}
                            {record.has_hwp_source && !record.has_hwp_result && (
                              <span className="aph-hwp-badge">한글 원본</span>
                            )}
                            {getCardTitle(record)}
                          </p>
                          <button
                            type="button"
                            className="sh-record-title-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartTitleEdit(record);
                            }}
                            title="기록 이름 수정"
                            aria-label="기록 이름 수정"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    <p className="sh-card-subtitle">{getCardSubtitle(record)}</p>
                  </div>
                  <div className="sh-card-right">
                    <span className="sh-card-date">{formatDate(record.created_at)}</span>
                    <span className="sh-card-count">{getRecordCountLabel(record)}</span>
                  </div>
                  <button
                    type="button"
                    className={`sh-card-toggle ${isExpanded ? 'sh-card-toggle--open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(record.id);
                    }}
                    aria-label={isExpanded ? '계획서 기록 접기' : '계획서 기록 펼치기'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                    </button>
                </div>

                {isExpanded && record.plan_structured && (
                  <div className="aph-expanded">
                    <div className="sh-expanded-meta">
                      <span>{record.subject || record.plan_structured?.subject || '교과'}</span>
                      <span className="sh-expanded-meta-sep">·</span>
                      <span>{record.grade || record.plan_structured?.grade || '학년'}</span>
                      <span className="sh-expanded-meta-sep">·</span>
                      <span>{getPlanTypeLabel(record)}</span>
                      {(record.item_count || 0) > 0 && (<><span className="sh-expanded-meta-sep">·</span><span>{getRecordCountLabel(record)}</span></>)}
                      {getRevisionCount(record) > 0 && (<><span className="sh-expanded-meta-sep">·</span><span>{getRevisionCount(record)}회 수정</span></>)}
                    </div>
                    {record.plan_structured.title && (
                      <h3 className="aph-plan-title">{record.plan_structured.title}</h3>
                    )}
                    {record.plan_structured.overview && (
                      <p className="aph-plan-overview">{safeOverviewText(record.plan_structured.overview)}</p>
                    )}
                    {record.web_search_sources?.length > 0 && (
                      <div className="aph-grounding-sources">
                        <SourcesButton
                          label="참고한 근거"
                          sources={record.web_search_sources}
                        />
                        <span>상세 모드 생성 시 확인한 자료예요</span>
                      </div>
                    )}

                    {/* 평가계획서: items 기반 렌더링 */}
                    {(!record.plan_type || record.plan_type === 'assessment') && record.plan_structured.items?.length > 0 && (
                      <>
                        {(() => {
                          const summary = getPointsSummary(record);
                          return summary.total > 0 ? (
                            <div className="assessment-points-summary aph-points-summary">
                              <div className="assessment-points-summary-items">
                                {Object.entries(summary.byCategory).map(([category, pts]) => (
                                  <span key={category} className="assessment-points-summary-chip">
                                    {category} <strong>{pts}점</strong>
                                  </span>
                                ))}
                                <span className="assessment-points-summary-total">
                                  합계 <strong>{summary.total}점</strong>
                                </span>
                              </div>
                            </div>
                          ) : null;
                        })()}
                        <div className="aph-items">
                          {record.plan_structured.items.map((planItem, pIdx) => renderAssessmentItem(record, planItem, pIdx))}
                        </div>
                      </>
                    )}

                    {/* 비-assessment 유형: plan_structured 기반 구조화 렌더링 */}
                    {record.plan_type && record.plan_type !== 'assessment' && record.plan_structured && (() => {
                      const ps = record.plan_structured;
                      const pt = record.plan_type;
                      const metaChips = [ps.subject, ps.grade, ps.semester, ps.unit, ps.lesson_number && `${ps.lesson_number}차시`, ps.total_sessions && `총 ${ps.total_sessions}차시`, ps.type, ps.project_name, ps.theme].filter(Boolean);
                      const standards = ps.achievement_standards || [];
                      const objectives = ps.learning_objectives || ps.objectives || [];
                      const hasStructuredContent = (pt === 'lesson_plan' && ps.lesson_flow?.length > 0)
                        || (pt === 'project' && (ps.grasps || ps.session_plan?.length > 0))
                        || (pt === 'essay_eval' && ps.items?.length > 0)
                        || (pt === 'inquiry' && (ps.core_ideas?.length > 0 || ps.inquiry_questions?.length > 0))
                        || (pt === 'autonomous' && ps.session_plan?.length > 0);

                      if (!hasStructuredContent && record.plan_text) {
                        return <pre className="aph-plan-text">{record.plan_text}</pre>;
                      }

                      return (
                        <div className="assessment-generic-result">
                          {metaChips.length > 0 && (
                            <div className="assessment-generic-meta">
                              {metaChips.map((c, i) => <span key={i} className="assessment-generic-meta-chip">{c}</span>)}
                            </div>
                          )}

                          {standards.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">성취기준</h3>
                              <ul className="assessment-generic-list">{standards.map((s, i) => <li key={i}>{s}</li>)}</ul>
                            </div>
                          )}

                          {objectives.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">{ps.learning_objectives ? '학습목표' : '교육목표'}</h3>
                              <ul className="assessment-generic-list">{objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
                            </div>
                          )}

                          {pt === 'lesson_plan' && ps.lesson_flow?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">수업 흐름</h3>
                              <div className="asr-timeline">
                                {ps.lesson_flow.map((step, i) => (
                                  <div key={i} className={`asr-timeline-step asr-phase-${step.phase === '도입' ? 'intro' : step.phase === '전개' ? 'main' : 'wrap'}`}>
                                    <div className="asr-timeline-header">
                                      <span className="asr-timeline-phase">{step.phase}</span>
                                      <span className="asr-timeline-duration">{step.duration}</span>
                                    </div>
                                    <div className="asr-timeline-body">
                                      {step.activities_teacher && <div className="asr-timeline-row"><span className="asr-timeline-label">교사</span><span className="asr-timeline-text">{step.activities_teacher}</span></div>}
                                      {step.activities_student && <div className="asr-timeline-row"><span className="asr-timeline-label">학생</span><span className="asr-timeline-text">{step.activities_student}</span></div>}
                                      {step.materials && <div className="asr-timeline-row"><span className="asr-timeline-label">자료</span><span className="asr-timeline-text">{step.materials}</span></div>}
                                      {step.notes && <div className="asr-timeline-row"><span className="asr-timeline-label">유의점</span><span className="asr-timeline-text">{step.notes}</span></div>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {ps.assessment_plan && <p className="assessment-generic-text" style={{ marginTop: 12 }}><strong>평가 계획:</strong> {ps.assessment_plan}</p>}
                              {ps.board_plan && <p className="assessment-generic-text"><strong>판서 계획:</strong> {ps.board_plan}</p>}
                            </div>
                          )}

                          {pt === 'project' && ps.grasps && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">GRASPS 설계</h3>
                              <div className="asr-grasps-grid">
                                {[['G', '목표', ps.grasps.goal], ['R', '역할', ps.grasps.role], ['A', '대상', ps.grasps.audience], ['S', '상황', ps.grasps.situation], ['P', '결과물', ps.grasps.product], ['S', '기준', ps.grasps.standards]].map(([letter, label, val], i) => (
                                  val && <div key={i} className="asr-grasps-card"><span className="asr-grasps-letter">{letter}</span><span className="asr-grasps-label">{label}</span><p className="asr-grasps-text">{val}</p></div>
                                ))}
                              </div>
                            </div>
                          )}
                          {pt === 'project' && ps.session_plan?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">차시별 계획</h3>
                              <div className="assessment-generic-cards">
                                {ps.session_plan.map((s, i) => (
                                  <div key={i} className="assessment-generic-card">
                                    <div className="asr-session-header"><strong>{s.session}차시</strong> {s.title}</div>
                                    <p className="assessment-generic-text">{s.activities}</p>
                                    {s.assessment && <p className="asr-session-assess">평가: {s.assessment}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {pt === 'essay_eval' && ps.items?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">평가 문항</h3>
                              <div className="assessment-generic-cards">
                                {ps.items.map((q, i) => (
                                  <div key={i} className="assessment-generic-card">
                                    <div className="asr-question-header"><span className="asr-question-type">{q.question_type}</span><span className="asr-question-points">{q.points}점</span></div>
                                    <p className="assessment-generic-text" style={{ fontWeight: 500 }}>{q.question}</p>
                                    {q.condition && <p className="asr-question-condition">조건: {q.condition}</p>}
                                    {q.scoring_criteria?.length > 0 && (
                                      <div className="asr-rubric">{q.scoring_criteria.map((c, ci) => (
                                        <div key={ci} className="asr-rubric-row"><span className="asr-rubric-level">{c.level}</span><span className="asr-rubric-score">{c.score_range}</span><span className="asr-rubric-desc">{c.description}</span></div>
                                      ))}</div>
                                    )}
                                    {q.example_answers?.length > 0 && (
                                      <div className="asr-examples">{q.example_answers.map((ea, ei) => (
                                        <div key={ei} className="asr-example"><span className="asr-example-level">{ea.level}</span><p className="asr-example-text">{ea.answer}</p></div>
                                      ))}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {pt === 'essay_eval' && ps.evaluation_elements?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">평가요소</h3>
                              <ul className="assessment-generic-list">{ps.evaluation_elements.map((e, i) => <li key={i}>{e}</li>)}</ul>
                            </div>
                          )}

                          {pt === 'inquiry' && ps.core_ideas?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">핵심 아이디어</h3>
                              <div className="assessment-generic-cards">
                                {ps.core_ideas.map((ci, i) => (
                                  <div key={i} className="assessment-generic-card">
                                    <p className="assessment-generic-text"><strong>핵심 아이디어:</strong> {ci.idea}</p>
                                    <p className="assessment-generic-text"><strong>핵심 문장:</strong> {ci.key_sentence}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {pt === 'inquiry' && ps.unit_question && (
                            <div className="asr-unit-question">
                              <span className="asr-unit-question-label">단원 핵심 질문</span>
                              <p className="asr-unit-question-text">{ps.unit_question}</p>
                            </div>
                          )}
                          {pt === 'inquiry' && ps.inquiry_questions?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">탐구질문</h3>
                              <div className="assessment-generic-cards">
                                {ps.inquiry_questions.map((iq, i) => (
                                  <div key={i} className="assessment-generic-card">
                                    <div className="asr-iq-header"><span className={`asr-iq-type asr-iq-type--${iq.type === '사실적' ? 'fact' : iq.type === '개념적' ? 'concept' : iq.type === '논쟁적' ? 'debate' : 'transfer'}`}>{iq.type}</span></div>
                                    <p className="assessment-generic-text" style={{ fontWeight: 500, fontSize: 15 }}>{iq.question}</p>
                                    {iq.suggested_activities && <p className="asr-iq-activity">추천 활동: {iq.suggested_activities}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {pt === 'autonomous' && ps.session_plan?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">차시별 계획</h3>
                              <div className="assessment-generic-cards">
                                {ps.session_plan.map((s, i) => (
                                  <div key={i} className="assessment-generic-card">
                                    <div className="asr-session-header"><strong>{s.session}차시</strong> {s.title}</div>
                                    <p className="assessment-generic-text">{s.content || s.activities}</p>
                                    {s.related_standards?.length > 0 && <p className="asr-session-assess">연계: {s.related_standards.join(', ')}</p>}
                                    {s.assessment && <p className="asr-session-assess">평가: {s.assessment}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {(pt === 'project' || pt === 'autonomous') && ps.scoring_criteria?.length > 0 && (
                            <div className="assessment-generic-section">
                              <h3 className="assessment-generic-section-title">평가기준</h3>
                              <div className="assessment-generic-cards">
                                {ps.scoring_criteria.map((c, i) => (
                                  <div key={i} className="assessment-generic-card"><strong>{c.level}</strong><p className="assessment-generic-text">{c.description}</p></div>
                                ))}
                              </div>
                            </div>
                          )}

                          {ps.teaching_connection && <div className="assessment-generic-section"><h3 className="assessment-generic-section-title">수업-평가 연계</h3><p className="assessment-generic-text">{ps.teaching_connection}</p></div>}
                          {ps.notes && <div className="assessment-generic-section"><h3 className="assessment-generic-section-title">유의사항</h3><p className="assessment-generic-text">{ps.notes}</p></div>}
                        </div>
                      );
                    })()}

                    {/* 비-assessment: plan_structured가 없으면 plan_text fallback */}
                    {record.plan_type && record.plan_type !== 'assessment' && !record.plan_structured && record.plan_text && (
                      <pre className="aph-plan-text">{record.plan_text}</pre>
                    )}

                    <div className="aph-action-area">
                      {record.plan_text && (
                        <button className="aph-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(record.plan_text); }}>
                          전체 복사
                        </button>
                      )}
                      <button
                        className="aph-continue-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/assessment-plan', {
                            state: {
                              continueRecord: {
                                id: record.id,
                                plan_type: record.plan_type || 'assessment',
                                plan_text: record.plan_text,
                                plan_structured: record.plan_structured,
                                analysis_mode: record.analysis_mode || 'basic',
                                previous_plan_text: record.previous_plan_text || '',
                                source_context_meta: record.source_context_meta || {},
                                source_plan_id: record.source_plan_id || null,
                                root_plan_id: record.root_plan_id || record.id,
                                initial_instructions: record.initial_instructions || '',
                                revision_history: record.revision_history || [],
                                web_search_status: record.web_search_status || 'not_requested',
                                web_search_sources: record.web_search_sources || [],
                                hwp_result: record.hwp_result || null,
                                hwp_source: record.hwp_source || null,
                              },
                            },
                          });
                        }}
                      >
                        이어서 수정하기
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {hasMore && historyData.length >= HISTORY_PAGE_SIZE && historyData.length < MAX_LOADED_HISTORY_RECORDS && (
            <button className="sh-load-more" onClick={handleLoadMore} disabled={isLoading}>
              {isLoading ? '불러오는 중...' : '더보기'}
            </button>
          )}
        </div>
      )}

      {showDeleteConfirm && (
        <div className="sh-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sh-modal-title">
              {selectedIds.length === 1 ? '이 기록을 삭제할까요?' : `${selectedIds.length}개 기록을 삭제할까요?`}
            </h3>
            <p className="sh-modal-desc">삭제하면 다시 복구할 수 없어요.</p>
            <div className="sh-modal-actions">
              <button className="sh-modal-btn sh-modal-btn--cancel" onClick={() => setShowDeleteConfirm(false)}>
                취소
              </button>
              <button className="sh-modal-btn sh-modal-btn--delete" onClick={handleDeleteSelected}>
                삭제하기
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AssessmentPlanHistory;
